// ./src/components/features/CreateEventWizard/useWizardFunnel.ts
//
// The wizard's side of the create-event FUNNEL (ROADMAP #62 P7): start, each step seen and completed (with the time on
// it), the event created, and how the wizard ended — finished, or abandoned on which step. The names and parameters live
// in `utils/analytics/funnel.ts`; this hook only decides WHEN.
//
// ⚠ A step is VIEWED only while the wizard is `active` — not behind the restore prompt (the host has not chosen yet,
//   and the screen behind it may be about to change) and not on the result screen.
// ⚠ ONE ending per run: `wizard_finish` or `wizard_abandon`, never both, and an abandon only after a start. Three exits
//   count as abandoning: the host confirming Leave (`close`), the tab closed or reloaded (`page_left`), and the wizard
//   UNMOUNTED by anything else — the ⌘K palette works over its bare shell (`navigated`).
// ⓘ A page restored from the back/forward cache is the same run coming back: its `page_left` ending is lifted, so a
//   later finish still counts (the data then holds both — read the LAST ending of a run).
// ⓘ Refs, not state: none of this may re-render the wizard, and React's dev double-run of effects must not send twice
//   (the start latches).
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  analytics,
  elapsedMs,
  eventCreateParams,
  wizardEndParams,
  wizardStartParams,
  wizardStepParams,
  WIZARD_EVENTS,
} from '@/utils/analytics';

export interface UseWizardFunnelArgs {
  /** A step is on screen for the host (no restore prompt, no result screen). */
  active: boolean;
  stepKey: string;
  stepIndex: number;
  stepCount: number;
  /** The host chose "Continue" on the restore prompt. */
  resumed: boolean;
  /** Arrived on a `?category=` link. */
  deepLink: boolean;
  /** The draft event exists (for the ending). */
  eventCreated: boolean;
  featureCount: number;
  /** #139 — the run is on a quick path (sent with how it ended). */
  quick: boolean;
  /** #148 — which quick path (`QUICK_KIND`), or null on the full wizard. */
  quickKind: string | null;
}

export interface WizardEventCreated {
  step: string;
  eventMode: string | null;
  eventType: string | null;
  parentType: string | null;
  eventFormat: string | null;
  features: number;
  quick: boolean;
  quickKind: string | null;
}

/** Milliseconds since the run started (0 before it has). */
const msSince = (startedAt: number | null): number => (startedAt == null ? 0 : elapsedMs(startedAt, Date.now()));

export const useWizardFunnel = (args: UseWizardFunnelArgs) => {
  const { active, stepKey } = args;
  // ⓘ The latest values, for callbacks and the pagehide listener that must not be re-created per render.
  const argsRef = useRef(args);
  argsRef.current = args;

  const startedAtRef = useRef<number | null>(null);
  const enteredRef = useRef<{ key: string; at: number } | null>(null);
  // ⓘ The step last reported as viewed: the same step twice in a row is ONE view (React's dev double-run; the result
  //   screen's retry). Going Back and Next again passes through another step, so it still counts.
  const lastViewedRef = useRef<string | null>(null);
  const endedRef = useRef(false);

  useEffect(() => {
    if (!active) {
      return;
    }

    const now = Date.now();
    const a = argsRef.current;

    if (startedAtRef.current == null) {
      startedAtRef.current = now;
      analytics.sendEvent(WIZARD_EVENTS.START, wizardStartParams({ resumed: a.resumed, deepLink: a.deepLink }));
    }

    if (lastViewedRef.current === stepKey) {
      return;
    }

    lastViewedRef.current = stepKey;
    enteredRef.current = { key: stepKey, at: now };
    analytics.sendEvent(WIZARD_EVENTS.STEP_VIEW, wizardStepParams({ step: stepKey, index: a.stepIndex, count: a.stepCount }));
  }, [active, stepKey]);

  /** The host left `key` FORWARD — Next, or Finish — after its save succeeded. */
  const stepCompleted = useCallback((key: string) => {
    const a = argsRef.current;
    const entered = enteredRef.current;
    const ms = entered && entered.key === key ? elapsedMs(entered.at, Date.now()) : undefined;

    analytics.sendEvent(WIZARD_EVENTS.STEP_COMPLETE, wizardStepParams({ step: key, index: a.stepIndex, count: a.stepCount, ms }));
  }, []);

  const eventCreated = useCallback((p: WizardEventCreated) => {
    analytics.sendEvent(WIZARD_EVENTS.EVENT_CREATE, eventCreateParams({ ...p, ms: msSince(startedAtRef.current) }));
  }, []);

  const finished = useCallback((step: string) => {
    if (endedRef.current) {
      return;
    }

    const a = argsRef.current;

    endedRef.current = true;
    analytics.sendEvent(WIZARD_EVENTS.FINISH, wizardEndParams({ step, ms: msSince(startedAtRef.current), created: true, features: a.featureCount, quick: a.quick, quickKind: a.quickKind }));
  }, []);

  /** `close` — the host confirmed leaving; `page_left` — the tab was closed or reloaded; `navigated` — unmounted otherwise. */
  const abandoned = useCallback((how: 'close' | 'page_left' | 'navigated') => {
    if (endedRef.current || startedAtRef.current == null) {
      return;
    }

    const a = argsRef.current;

    endedRef.current = true;
    analytics.sendEvent(WIZARD_EVENTS.ABANDON, {
      ...wizardEndParams({ step: a.stepKey, ms: msSince(startedAtRef.current), created: a.eventCreated, features: a.featureCount, quick: a.quick, quickKind: a.quickKind }),
      how,
    });
  }, []);

  useEffect(() => {
    const onPageHide = () => abandoned('page_left');
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        endedRef.current = false;
      }
    };

    // ⓘ Capture: at the window, capturing listeners run first — before gtag.js's own unload handling.
    window.addEventListener('pagehide', onPageHide, { capture: true });
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pagehide', onPageHide, { capture: true });
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [abandoned]);

  // An exit by unmounting. ⚠ Deferred, and cancelled by a re-run: React's dev double-run unmounts and remounts at once,
  //   which must not end the run.
  const unmountTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (unmountTimerRef.current != null) {
      window.clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }

    return () => {
      unmountTimerRef.current = window.setTimeout(() => abandoned('navigated'), 0);
    };
  }, [abandoned]);

  // ⓘ One stable object, so the wizard's callbacks that use it are not re-created every render.
  return useMemo(() => ({ stepCompleted, eventCreated, finished, abandoned }), [stepCompleted, eventCreated, finished, abandoned]);
};
